// Ink: what turns a stream of pointer samples into the line the player sees.
// Pure — no DOM, no clock, no randomness — so it can be tested on its own.
// Two stages doing two different jobs:
//
//   1. `OneEuroFilter2D` (per sample, time domain) kills the jitter of a hand
//      that is nearly still. It is speed-adaptive, so it costs almost no lag
//      while the finger is moving — but for the same reason it lets tremor
//      through exactly when the finger *is* moving, which is most of a stroke.
//   2. `smoothStroke` (per stroke, arc-length domain) takes that out. Tremor
//      while drawing is a wobble of a fairly fixed wavelength *in space*
//      (~10-30 px), not in time, so it is filtered in space: resample the
//      stroke to an even spacing, then blur it along its own length.
//
// Filtering in space rather than in time is what makes this feel like a
// drawing tool: the result does not depend on how fast the stroke was drawn,
// and it adds no lag to the part already drawn — only the last few points,
// still under the finger, see a one-sided window, so the line settles behind
// the finger as you draw past it.
//
// How much smoothing is deliberate. `SMOOTH_RADIUS_PX` is set so a hand-drawn
// 90° corner comes out as an arc of roughly 11 px radius — well inside the
// 40-50 px minimum turn radius of every aircraft in the game (`turnRadius`,
// src/sim/aircraft.ts). So the line is clean, and a corner drawn tighter than
// an aircraft can bank is still a corner it cannot fly: guessing the physics
// stays the game. This removes the shake, not the shape.
import type { Vec } from '../sim/index.ts';
import { resample } from '../sim/index.ts';
import { OneEuroFilter2D } from './oneEuro.ts';

/** minimum distance (logical px) between two recorded samples */
export const MIN_SPACING = 4;
/** arc-length spacing (logical px) the stroke is resampled to before blurring —
 *  also the waypoint density the sim is handed, so it never depends on draw speed */
export const INK_SPACING = 6;
/** half-width of the blur along the stroke, logical px */
const SMOOTH_RADIUS_PX = 18;
/** ...as a count of resampled points */
const SMOOTH_RADIUS = Math.round(SMOOTH_RADIUS_PX / INK_SPACING);
/** Gaussian sigma in points — half the radius puts the window edge at 2σ */
const SMOOTH_SIGMA = SMOOTH_RADIUS / 2;
/** one-sided kernel, index = distance in points; built once */
const WEIGHTS = Array.from(
  { length: SMOOTH_RADIUS + 1 },
  (_, k) => Math.exp(-(k * k) / (2 * SMOOTH_SIGMA * SMOOTH_SIGMA)),
);

/**
 * Blur a polyline along its own length. The window is truncated *symmetrically*
 * at both ends (`r` shrinks to fit), which pins the first and last point exactly
 * and tapers the smoothing into them — so the line still starts where the finger
 * went down and ends where it is now, and neither end creeps inward.
 */
function blurAlong(pts: Vec[]): Vec[] {
  const n = pts.length;
  const out: Vec[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const r = Math.min(SMOOTH_RADIUS, i, n - 1 - i);
    if (r === 0) { out[i] = { x: pts[i].x, y: pts[i].y }; continue; }
    let sx = pts[i].x * WEIGHTS[0], sy = pts[i].y * WEIGHTS[0], sw = WEIGHTS[0];
    for (let k = 1; k <= r; k++) {
      const w = WEIGHTS[k];
      sx += (pts[i - k].x + pts[i + k].x) * w;
      sy += (pts[i - k].y + pts[i + k].y) * w;
      sw += 2 * w;
    }
    out[i] = { x: sx / sw, y: sy / sw };
  }
  return out;
}

/**
 * A drawn line as ink: evenly spaced along its length, blurred along it, both
 * endpoints exact. Neither `pts` nor its points are mutated.
 */
export function smoothStroke(pts: Vec[]): Vec[] {
  if (pts.length < 3) return pts.map(v => ({ x: v.x, y: v.y }));
  return blurAlong(resample(pts, INK_SPACING));
}

/**
 * One stroke in progress. Feed it raw pointer samples, ask for `points()` to
 * preview and `commit()` when the finger lifts. The same smoothing runs on
 * both, so the line the aircraft is handed is the line the player watched
 * themselves draw.
 */
export class InkStroke {
  private readonly filter = new OneEuroFilter2D();
  private readonly samples: Vec[] = [];
  private tip: Vec | null = null;

  /** `tSec` is the event's own timestamp in seconds (`event.timeStamp / 1000`). */
  push(x: number, y: number, tSec: number): void {
    const p = this.filter.filter(x, y, tSec);
    this.tip = p;
    const last = this.samples[this.samples.length - 1];
    if (!last || Math.hypot(p.x - last.x, p.y - last.y) > MIN_SPACING) this.samples.push({ x: p.x, y: p.y });
  }

  /** how many samples have been kept — the tap-vs-drag test reads this */
  get length(): number { return this.samples.length; }

  /** the stroke so far, ending under the finger */
  points(): Vec[] { return smoothStroke(this.withTip()); }

  /**
   * The finished stroke, ending exactly at `end` — the *unfiltered* release
   * position. The 1€ filter lags on a fast flick and that lag must never cost
   * a runway or pad snap at the one moment it matters; as the polyline's last
   * point it survives both the resample and the blur untouched.
   */
  commit(end: Vec): Vec[] { return smoothStroke([...this.samples, { x: end.x, y: end.y }]); }

  /** samples plus the live filtered position, when that is somewhere new */
  private withTip(): Vec[] {
    const last = this.samples[this.samples.length - 1];
    if (!this.tip || (last && this.tip.x === last.x && this.tip.y === last.y)) return this.samples;
    return [...this.samples, this.tip];
  }
}
